import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { LANGUAGE_LABELS } from '../model/constants';

export default function AdditionalContentModal({
  open,
  languages,
  value,
  onChange,
  onClose,
  onSave,
  saving,
  availableEntries = [],
  onUseExisting,
}) {
  const [mode, setMode] = useState('existing');
  const [selectedEntryId, setSelectedEntryId] = useState('');
  const [addingExisting, setAddingExisting] = useState(false);
  useEffect(() => {
    if (open) {
      setMode(availableEntries.length ? 'existing' : 'new');
      setSelectedEntryId('');
    }
  }, [open, availableEntries.length]);
  const busy = saving || addingExisting;
  const useExisting = async () => {
    if (!selectedEntryId || busy) return;
    setAddingExisting(true);
    try {
      const added = await onUseExisting(selectedEntryId);
      if (added) onClose();
    } finally {
      setAddingExisting(false);
    }
  };
  const visibleLanguages = languages.length > 0 ? languages : ['en'];
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/40 px-4">
      <div role="dialog" aria-modal="true" aria-labelledby="add-text-box-heading" className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-2xl bg-white shadow-2xl">
        <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-5 py-4">
          <div>
            <h2 id="add-text-box-heading" className="text-base font-semibold text-slate-900">Add another text box</h2>
            <p className="text-xs text-slate-500">Choose unused text from this page or create a new text box.</p>
          </div>
          <button
            type="button"
            disabled={busy}
            onClick={onClose}
            className="rounded-full p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-900"
            title="Close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="grid grid-cols-2 gap-2 px-5 pt-5">
          <button type="button" disabled={busy} aria-pressed={mode === 'existing'} onClick={() => setMode('existing')} className={`rounded-lg border px-3 py-2 text-sm font-semibold ${mode === 'existing' ? 'border-blue-300 bg-blue-50 text-blue-800' : 'border-slate-200 text-slate-700'}`}>Use existing text</button>
          <button type="button" disabled={busy} aria-pressed={mode === 'new'} onClick={() => setMode('new')} className={`rounded-lg border px-3 py-2 text-sm font-semibold ${mode === 'new' ? 'border-blue-300 bg-blue-50 text-blue-800' : 'border-slate-200 text-slate-700'}`}>Create new text</button>
        </div>
        {mode === 'existing' ? <div className="space-y-3 px-5 py-5">
          <label htmlFor="existing-page-text" className="block text-sm font-semibold text-slate-900">Text box name</label>
          <select id="existing-page-text" disabled={busy || !availableEntries.length} value={selectedEntryId} onChange={(event) => setSelectedEntryId(event.target.value)} className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm">
            <option value="">Choose existing text…</option>
            {availableEntries.map((entry) => <option key={entry.id} value={entry.id}>{entry.label}{entry.hidden ? ' (hidden)' : ''}</option>)}
          </select>
          {!availableEntries.length && <p className="text-sm text-slate-500">All existing text is already on the canvas. Create new text instead.</p>}
          {selectedEntryId && <p className="text-xs text-slate-500">Uses this page’s wording. Hidden boxes keep their position and formatting.</p>}
        </div> : <div className="space-y-4 px-5 py-5">
          {visibleLanguages.map((language) => (
            <section key={language} className="border-t border-slate-200 pt-4 first:border-t-0 first:pt-0">
              <label className="block text-sm font-semibold text-slate-900">
                {LANGUAGE_LABELS[language] || language.toUpperCase()} Text
              </label>
              <textarea
                value={value.texts?.[language] || ''}
                onChange={(event) => onChange({
                  ...value,
                  texts: {
                    ...(value.texts || {}),
                    [language]: event.target.value,
                  },
                })}
                rows={4}
                className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-slate-400"
              />
            </section>
          ))}

          <div className="border-t border-slate-200 pt-4">
            <label className="block text-sm font-semibold text-slate-900">
              Text box name
            </label>
            <input
              value={value.label}
              onChange={(event) => onChange({
                ...value,
                label: event.target.value,
              })}
              className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-slate-400"
              placeholder="For example: Instructions or Copyright"
            />
          </div>
        </div>}

        <div className="flex justify-end gap-2 border-t border-slate-200 px-5 py-4">
          <button
            type="button"
            disabled={busy}
            onClick={onClose}
            className="rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={mode === 'existing' ? useExisting : onSave}
            disabled={busy || (mode === 'existing' && !selectedEntryId)}
            className="rounded-full bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {busy ? 'Adding…' : mode === 'existing' ? 'Add to layout' : 'Create text box'}
          </button>
        </div>
      </div>
    </div>
  );
}
